import React, { createContext, useContext, useEffect, useState } from 'react';
import { CORE_API_URL } from './config';

// FLAGS names the keys the frontend reads. Constants rather than string
// literals at call sites, so a typo is a missing export rather than a flag that
// silently reads false forever.
export const FLAGS = {
  publicSignup: 'feature.public_signup',
  publicContact: 'feature.public_contact',
  candidateGraph: 'feature.candidate_graph',
  targetedOutreach: 'feature.targeted_outreach',
  signalSources: 'feature.signal_sources',
  deepResearch: 'feature.deep_research',
};

// How long one attempt may take before it is abandoned. Without this a hung
// request never resolves and never rejects, so readiness is never reached and
// a caller waiting on useFlagsReady waits forever.
const FETCH_TIMEOUT_MS = 8000;

// The context holds BOTH the resolved map and whether it has been answered.
// `ready:false` is not "every flag is off" -- it is "no answer yet".
// ProtectedRoute is the caller that needs the distinction: a route with a
// `flag` prop waits for `ready` before it will deny, rendering a neutral
// placeholder instead, so an unanswered flag never reads as "off" and flashes
// NotFound over a page the user may be entitled to. useFlag alone still can't
// tell the two cases apart -- it stays unchanged by readiness by design (see
// its own comment below) -- which is exactly what useFlagsReady is for.
const FlagContext = createContext({ flags: null, ready: false });

async function fetchFlags() {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const response = await fetch(`${CORE_API_URL}/api/public/config`, { signal: controller.signal });
    if (!response.ok) throw new Error(`config responded ${response.status}`);
    const body = await response.json();
    return body.flags || {};
  } finally {
    clearTimeout(timer);
  }
}

// FlagProvider fetches /api/public/config once at boot.
//
// FAIL-CLOSED ON PURPOSE: if the fetch fails for any reason -- network error,
// non-2xx, timeout, or no CORE_API_URL configured at all -- every flag reads
// false.
//
// That is not a defensive nicety, it is how this preserves today's production
// behaviour. A production bundle has no Go API to call, so the fetch fails, so
// the nav hides exactly the entries a missing CORE_API_URL used to hide.
// Defaulting to "on" would ship links to pages that cannot load.
//
// It retries ONCE before giving up: a single dropped request should not cost
// a user a whole feature's worth of nav entries, but fail-closed still has to
// stay fast, so it stops at one retry rather than looping.
export function FlagProvider({ children }) {
  const [state, setState] = useState({ flags: null, ready: false });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!CORE_API_URL) {
        if (!cancelled) setState({ flags: {}, ready: true });
        return;
      }
      try {
        const flags = await fetchFlags();
        if (!cancelled) setState({ flags, ready: true });
        return;
      } catch (first) {
        try {
          const flags = await fetchFlags();
          if (!cancelled) setState({ flags, ready: true });
          return;
        } catch (second) {
          console.warn('SignalCue: feature configuration unavailable, treating every flag as off:', second.message);
          // READY, not stuck. An outage resolves to all-off, which is the
          // fail-closed answer; leaving ready false would hang the app instead.
          if (!cancelled) setState({ flags: {}, ready: true });
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, []);

  return <FlagContext.Provider value={state}>{children}</FlagContext.Provider>;
}

// useFlag answers whether a feature is on. Strict equality against true, so a
// server that ever sent a string or a number cannot make a flag truthy by
// accident. Unchanged by readiness: an unanswered flag still reads false, and
// a caller that must not act on that instead should ask useFlagsReady too.
export function useFlag(key) {
  const { flags } = useContext(FlagContext);
  if (!flags) return false;
  return flags[key] === true;
}

// useFlagsReady reports whether the configuration has been answered at all --
// successfully or not. ProtectedRoute is its caller: a route that carries a
// `flag` prop must DENY on a false flag (rather than merely decorate, the way
// the "Soon" badge does today), so it waits on useFlagsReady first rather than
// treating "haven't heard back" as "off".
export function useFlagsReady() {
  return useContext(FlagContext).ready;
}
